import Choice, { ChoiceItem } from "@ingka/choice";
import Modal, { Theatre } from '@ingka/modal';
import SkapaTable, { TableBody as SkapaTableBody, TableHeader as SkapaTableHeader } from '@ingka/table';
import { StyledToolbarButton, StyledTooltip } from "../../../../shared/styles/components/integrations.styles";

import APIService from '../../../../services/api.service';
import Button from '@ingka/button';
import Checkbox from '@ingka/checkbox';
import { CloseIconButton } from "../../../../shared/components/IconButton";
import { LoadingIndicator } from "../../../../shared/components/TableLoadingIndicator";
import { ModalCloseButton } from "../../../../shared/styles/components/modal.styles";
import { SubItem } from "../../types/sub-item.types";
import { ToastService } from "../../../../services/toast.service";
import clipboardCheckmark from '@ingka/ssr-icon/paths/clipboard-checkmark';
import styled from '@emotion/styled';
import { theme } from "../../../../shared/styles/core/theme";
import { tokens } from "../../../../shared/styles/core/tokens";
import { useCopyParts } from '../../state/copy-parts.state';
import { useParentItemsFromSelectedSubItems } from '../../state/selection.state';
import { useQueryClient } from 'react-query';
import { useState } from 'react';
import { useTableDataState } from '../../state/table-data.state';
import useToggle from '../../../../shared/hooks/useToggle';

type PasteType = {
   text: string;
   description: string;
}

const PASTE_TYPES: {
   Mass: PasteType;
   Volume: PasteType
} = {
   Mass: {
      text: 'Copy by mass',
      description: 'TODO: Copy by mass description'
   },
   Volume: {
      text: 'Copy by volume',
      description: 'TODO: Copy by volume description'
   }
}

const generatePartIdentifier = (subItem: SubItem) => `${subItem.part}-${subItem.matSetName}-${subItem.partDetail}-${subItem.materialGrade}-${subItem.parentId || 'no parent'}`

export const PasteButton = () => {
   const queryClient = useQueryClient();
   const query = useTableDataState((state) => state.query);
   const selectedItems = useParentItemsFromSelectedSubItems(true);
   const { copiedParts, familyName } = useCopyParts()
   const [isOpen, toggleIsOpen] = useToggle(false)

   const [verifiedPartsSelectedForCopy, setVerifiedParts] = useState<string[]>([])
   const [verifiedItemsSelectedForPaste, setVerifiedItems] = useState<string[]>([])
   const [selectedPasteType, setSelectedPasteType] = useState<keyof typeof PASTE_TYPES>('Mass')

   const resolvedSelectedItems = selectedItems
      .filter((item) => !copiedParts
         .map((cp) => cp.parentId)
         .includes(item.itemNo))

   const isSelectedWithinSameFamily = resolvedSelectedItems.every((item) => item.familyName === familyName);

   const tooltipContent = isSelectedWithinSameFamily ? 'Paste part(s) into selected items' : 'Selected items is not within the same family as copied content'

   const onClick = () => {
      setVerifiedParts(copiedParts.map(generatePartIdentifier))
      setVerifiedItems(resolvedSelectedItems.map((i) => i.itemNo))
      toggleIsOpen(true)
   };

   const submitCopyPaste = async () => {
      const finalPartsToCopy = copiedParts.filter((subItem) => verifiedPartsSelectedForCopy.includes(generatePartIdentifier(subItem)))
      const itemsFrom = finalPartsToCopy.map((subItem) => ({
         itemNo: subItem.parentId,
         materialSetName: subItem.matSetName,
         id: subItem.id,
      }))

      APIService.copyParts({
         itemsFrom,
         itemNos: verifiedItemsSelectedForPaste,
         pasteType: selectedPasteType,
      }).then((res) => {
         const nrOfCopiedPartsSuccessful = (res.supplierMaterialCopyPaste.itemsFrom || []).filter((item) => item.status === 'Ok').length
         const nrOfPasteToItemsSuccessful = (res.supplierMaterialCopyPaste.itemNos || []).reduce<string[]>((curr, item) => {
            if (item.status !== 'Ok' || curr.includes(item.itemNo)) {
               return curr
            }

            return [...curr, item.itemNo]
         }, []).length

         if (nrOfCopiedPartsSuccessful + nrOfPasteToItemsSuccessful === 0) {
            ToastService.info('No parts was copied')
         } else {
            ToastService.success(`${nrOfCopiedPartsSuccessful} part(s) copied into ${nrOfPasteToItemsSuccessful} item(s)`)
         }

         queryClient.invalidateQueries(['data', query])
         toggleIsOpen(false)
      }).catch((e) => {
         console.log(e)
         ToastService.error('Something went wrong. Could not copy parts')
      })
   }

   return (
      copiedParts.length > 0 && resolvedSelectedItems.length > 0 ?
         <>
            <StyledTooltip title={tooltipContent}>
               <StyledToolbarButton
                  ssrIcon={clipboardCheckmark}
                  text="Filter"
                  iconOnly={true}
                  small={true}
                  disabled={!isSelectedWithinSameFamily}
                  onClick={onClick}
               >
                  <CountBadge>{copiedParts.length}</CountBadge>
               </StyledToolbarButton>
            </StyledTooltip>
            {isOpen && <Modal visible={isOpen} handleCloseBtn={() => toggleIsOpen(false)}>
               <StyledTheatre header={
                  <StyledHeaderDiv>
                     <h2>Copy parts<LoadingIndicator /></h2>
                     <div>Paste copied parts to selected items</div>
                     <ModalCloseButton>
                        <CloseIconButton onClick={() => toggleIsOpen(false)} />
                     </ModalCloseButton>
                  </StyledHeaderDiv>
               }>
                  <StyledTheatreContentDiv>
                     <div style={{ overflow: 'auto' }}>
                        <p><b>Copied parts</b></p>
                        <SkapaTable fullWidth>
                           <SkapaTableHeader>
                              <tr>
                                 <th style={{ width: 0, whiteSpace: 'nowrap' }}>Paste part</th>
                                 <th>Part</th>
                                 <th>MatSet name</th>
                                 <th>Part detail</th>
                                 <th>Material Grade</th>
                                 <th>3D Number</th>
                                 <th>Mat. part qty</th>
                                 <th>Mass (g)</th>
                                 <th>Volume (mm³)</th>
                              </tr>
                           </SkapaTableHeader>

                           <SkapaTableBody>
                              {copiedParts.map((subItem) => {
                                 const selectionId = generatePartIdentifier(subItem)
                                 const isSelected = verifiedPartsSelectedForCopy.includes(selectionId)
                                 const toggleSelection = () => {
                                    if (isSelected) {
                                       setVerifiedParts(verifiedPartsSelectedForCopy.filter((p) => p !== selectionId))
                                    } else {
                                       setVerifiedParts([
                                          ...verifiedPartsSelectedForCopy,
                                          selectionId,
                                       ])
                                    }
                                 }

                                 return (
                                    <tr>
                                       <td>
                                          <Checkbox id={selectionId} value={selectionId} checked={isSelected} onChange={toggleSelection} />
                                       </td>
                                       <td>{subItem.part}</td>
                                       <td>{subItem.matSetName}</td>
                                       <td>{subItem.partDetail}</td>
                                       <td>{subItem.materialGrade}</td>
                                       <td>{subItem.threeDModelNo}</td>
                                       <td>{subItem.quantity}</td>
                                       <td>{subItem.mass ?? 0}</td>
                                       <td>{subItem.volume ?? 0}</td>
                                    </tr>
                                 )
                              })}
                           </SkapaTableBody>
                        </SkapaTable>
                     </div>

                     <div style={{ maxWidth: '24em' }}>
                        <Choice preselectedIndex={0} onSelect={(arg: keyof typeof PASTE_TYPES) => {
                           setSelectedPasteType(arg)
                        }} size='small'>
                           {Object.entries(PASTE_TYPES)
                              .map(([key, { text, description }]) => <ChoiceItem id={key} title={text} caption={description} />)}
                        </Choice>
                     </div>

                     <div style={{ overflow: 'auto' }}>
                        <p><b>Paste to items</b></p>
                        <SkapaTable fullWidth>
                           <SkapaTableHeader>
                              <tr>
                                 <th style={{ width: 0, whiteSpace: 'nowrap' }}>Paste to</th>
                                 <th>Item number</th>
                                 <th>HFB</th>
                                 <th>PA</th>
                                 <th>Family name</th>
                                 <th>Item name</th>
                              </tr>
                           </SkapaTableHeader>

                           <SkapaTableBody>
                              {resolvedSelectedItems.map((item) => {
                                 const isSelected = verifiedItemsSelectedForPaste.includes(item.itemNo)
                                 const toggleSelection = () => {
                                    if (isSelected) {
                                       setVerifiedItems(verifiedItemsSelectedForPaste.filter((itemNo) => itemNo !== item.itemNo))
                                    } else {
                                       setVerifiedItems([
                                          ...verifiedItemsSelectedForPaste,
                                          item.itemNo,
                                       ])
                                    }
                                 }

                                 return (
                                    <tr>
                                       <td>
                                          <Checkbox id={item.itemNo} value={item.itemNo} checked={isSelected} onChange={toggleSelection} />
                                       </td>
                                       <td>{item.itemNo}</td>
                                       <td>{item.hfb}</td>
                                       <td>{item.pa}</td>
                                       <td>{item.familyName}</td>
                                       <td>{item.itemName}</td>
                                    </tr>
                                 )
                              })}
                           </SkapaTableBody>
                        </SkapaTable>
                     </div>

                     <div style={{ marginTop: 'auto' }}>
                        <Button type='emphasised' onClick={submitCopyPaste}>Copy</Button>
                     </div>
                  </StyledTheatreContentDiv>

               </StyledTheatre>
            </Modal>}
         </> : <></>
   );
};

const CountBadge = styled.div`
  position: absolute;
  top: -0.5em;
  left: calc(50% + 1em);
  background: ${theme.colors.state.error};
  color: ${theme.colors.text.inverse};
  padding: ${tokens.space.sm} ${tokens.space.sm};
  min-width: 2em;
  height: 2em;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${tokens.radius.pill};
  font-size: ${tokens.font.size.sm};
  z-index: ${tokens.zIndex.base};
`

const StyledHeaderDiv = styled.div`
   padding: ${tokens.space['3xl']} ${tokens.space['3xl']} ${tokens.space.xl};
`;

const StyledTheatreContentDiv = styled.div`
   padding: 0 ${tokens.space['3xl']} ${tokens.space['3xl']};
   display: grid;
   grid-template-rows: 1fr auto 1fr auto;
   overflow: hidden;
   gap: ${tokens.space.md};
`;

const StyledTheatre = styled(Theatre)`
   & > .theatre__content-wrapper {
      display: grid;
      grid-template-rows: auto 1fr;
   }
`